import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PortalLayout from '../../components/PortalLayout';
import PortalKbSuggestions from '../../components/PortalKbSuggestions';
import DynamicFieldInput from '../../components/DynamicFieldInput';
import { submitPublicTicket, getPublicTicketProject, getPublicProjectFields, errorMessage } from '../../services/customerApi';
import { useLanguage } from '../../i18n/LanguageContext';
import { getRecaptchaToken } from '../../utils/recaptcha';
import { resolveLabel } from '../../utils/fieldLabels';

const emptyForm = { name: '', email: '', subject: '', description: '' };

function PortalNewTicket() {
  const { t } = useLanguage();
  const { projectSlug } = useParams();
  const [form, setForm] = useState(emptyForm);
  const [project, setProject] = useState(null);
  const [dynamicFields, setDynamicFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  useEffect(() => {
    getPublicTicketProject(projectSlug)
      .then((data) => setProject(data.project))
      .catch(() => {});
    getPublicProjectFields(projectSlug)
      .then((data) => setDynamicFields(data.fields || []))
      .catch(() => setDynamicFields([]));
  }, [projectSlug]);

  // Validazione custom al posto di quella nativa del browser (niente più
  // tooltip fuori stile "Compila questo campo": bordo rosso + messaggio
  // sotto il campo, vedi DynamicFieldInput, bastano da soli).
  const validateRequiredFields = () => {
    const errors = {};
    dynamicFields.forEach((field) => {
      if (!field.is_required) return;
      const key = field.core_key || field.id;
      if (field.field_type === 'attachment') {
        const val = fieldValues[field.id];
        if (!Array.isArray(val) || val.length === 0) errors[key] = t('portal.fieldRequired');
        return;
      }
      const value = field.core_key ? form[field.core_key] : fieldValues[field.id];
      if (field.field_type === 'checkbox') {
        if (value !== '1' && value !== true) errors[key] = t('portal.fieldRequired');
        return;
      }
      if (!value || !String(value).trim()) errors[key] = t('portal.fieldRequired');
    });
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateRequiredFields();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const recaptchaToken = await getRecaptchaToken('submit_ticket');

      // I campi "attachment" portano un array di File in fieldValues (vedi
      // DynamicFieldInput): vanno tenuti fuori dal JSON di field_values
      // (che il backend valida come stringhe) e inviati come multipart solo
      // se ce n'è almeno uno, per non appesantire il caso comune senza allegati.
      const attachmentFieldIds = new Set(
        dynamicFields.filter((f) => f.field_type === 'attachment').map((f) => f.id)
      );
      const textFieldValues = {};
      const files = [];
      Object.entries(fieldValues).forEach(([fieldId, value]) => {
        if (attachmentFieldIds.has(Number(fieldId))) {
          (value || []).forEach((file) => files.push(file));
        } else {
          textFieldValues[fieldId] = value;
        }
      });

      let payload;
      if (files.length > 0) {
        payload = new FormData();
        payload.append('project_slug', projectSlug);
        payload.append('name', form.name || '');
        payload.append('email', form.email || '');
        payload.append('subject', form.subject || '');
        payload.append('description', form.description || '');
        payload.append('field_values', JSON.stringify(textFieldValues));
        payload.append('recaptcha_token', recaptchaToken || '');
        files.forEach((file) => payload.append('attachments[]', file));
      } else {
        payload = {
          ...form,
          project_slug: projectSlug,
          field_values: textFieldValues,
          recaptcha_token: recaptchaToken,
        };
      }

      const res = await submitPublicTicket(payload);
      setResult({ ...res.ticket, attachments: res.attachments || [] });
    } catch (err) {
      setError(errorMessage(err, t('portal.submitError')));
    } finally {
      setSubmitting(false);
    }
  };

  const visitorLocale = typeof navigator !== 'undefined' ? navigator.language : null;
  const defaultLanguage = project?.default_language || 'it-IT';
  const preferredLocale = project?.languages?.includes(visitorLocale) ? visitorLocale : defaultLanguage;
  const portalTitle = resolveLabel(project?.portal_title, preferredLocale, defaultLanguage) || t('portal.newTicketTitle');
  const portalSubtitle = resolveLabel(project?.portal_subtitle, preferredLocale, defaultLanguage) || t('portal.newTicketSubtitle');

  if (result) {
    return (
      <PortalLayout title={t('portal.submitSuccessTitle')} accent={project?.color}>
        <div className="card portal-success">
          <div className="card-body">
            <span className="portal-success-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            </span>
            <p className="portal-success-text">{t('portal.submitSuccessBody')}</p>
            {result.attachments.length > 0 && (
              <ul className="attachment-file-list portal-files">
                {result.attachments.map((a) => (
                  <li key={a.id}>
                    <span>{a.original_filename}</span>
                  </li>
                ))}
              </ul>
            )}
            <Link className="btn portal-cta" to={`/portal/ticket/${result.public_token}`}>
              {t('portal.viewTicket')}
            </Link>
          </div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout title={portalTitle} subtitle={portalSubtitle} accent={project?.color}>
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmit} className="card" noValidate>
        <div className="card-body">
          {dynamicFields.map((field) =>
            field.core_key ? (
              <DynamicFieldInput
                key={field.id}
                field={field}
                value={form[field.core_key]}
                onChange={(value) => {
                  clearFieldError(field.core_key);
                  setForm({ ...form, [field.core_key]: value });
                }}
                locale={preferredLocale}
                fallbackLocale={defaultLanguage}
                error={fieldErrors[field.core_key]}
              />
            ) : (
              <DynamicFieldInput
                key={field.id}
                field={field}
                value={fieldValues[field.id]}
                onChange={(value) => {
                  clearFieldError(field.id);
                  setFieldValues({ ...fieldValues, [field.id]: value });
                }}
                locale={preferredLocale}
                fallbackLocale={defaultLanguage}
                error={fieldErrors[field.id]}
              />
            )
          )}
          <PortalKbSuggestions slug={projectSlug} text={`${form.subject || ''} ${(form.description || '').slice(0, 80)}`} />
          <button type="submit" className="btn" disabled={submitting}>
            {submitting ? t('portal.submitting') : t('portal.submit')}
          </button>
        </div>
      </form>
      <p className="portal-note">
        {t('portal.alreadyHaveAccount')} <Link to={`/portal/${projectSlug}/login`}>{t('portal.login')}</Link>
      </p>
    </PortalLayout>
  );
}

export default PortalNewTicket;
